
import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X, Search, Heart, UserCircle, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { useAuth } from "@/contexts/AuthContext";
import { products } from "@/data/products";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { totalItems } = useCart();
  const { totalItems: wishlistCount } = useWishlist();
  const { user, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
    setSearchQuery("");
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchResults = searchQuery.length > 1
    ? products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.category.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 5)
    : [];

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/shop", label: "Shop" },
    { to: "/about", label: "About" },
    { to: "/blog", label: "Journal" },
    { to: "/ingredients", label: "Ingredients" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-bunnie-cream/95 backdrop-blur-md shadow-md py-2"
          : "bg-transparent py-4"
      }`}
    >
      <nav className="container-custom flex flex-col items-center">
        {/* Logo */}
        <div className="w-full flex justify-center mb-3">
          <Link to="/" className="flex flex-col items-center animate-fade-in">
            <img
              src="/lovable-uploads/2f352dc2-918a-4a3e-aa83-90fa5faeb95a.webp"
              alt="Bunnie Essience Logo"
              className="h-16 mb-1"
            />
            <div className="flex flex-col items-center">
              <span className="font-serif text-bunnie-brown font-medium text-lg tracking-wide">BUNNIE ESSIENCE</span>
              <span className="text-bunnie-brown/80 text-xs">Clean Hair & Skin Care</span>
            </div>
          </Link>
        </div>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8 py-2 mt-1">
          {navLinks.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-bunnie-brown transition-all relative after:absolute after:bottom-[-4px] after:left-0 after:w-full after:h-[2px] after:bg-bunnie-brown after:origin-left after:transition-transform after:duration-300 ${
                location.pathname === link.to ? "after:scale-x-100 font-medium" : "after:scale-x-0 hover:after:scale-x-100"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Desktop Icons */}
        <div className="hidden md:flex items-center gap-2 absolute top-6 right-8">
          <div ref={searchRef} className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSearchOpen(!searchOpen)}
              className="text-bunnie-brown"
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </Button>
            <AnimatePresence>
              {searchOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, width: 0 }}
                  animate={{ opacity: 1, y: 0, width: 280 }}
                  exit={{ opacity: 0, y: -10, width: 0 }}
                  className="absolute right-0 top-12 bg-white rounded-lg shadow-xl border border-bunnie-brown/10 overflow-hidden"
                >
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-3 text-sm focus:outline-none"
                    autoFocus
                  />
                  {searchResults.length > 0 && (
                    <div className="border-t border-bunnie-brown/10 max-h-64 overflow-y-auto">
                      {searchResults.map(product => (
                        <button
                          key={product.id}
                          onClick={() => {
                            navigate(`/product/${product.id}`);
                            setSearchOpen(false);
                            setSearchQuery("");
                          }}
                          className="w-full flex items-center gap-3 p-3 hover:bg-bunnie-peach/10 transition-colors text-left"
                        >
                          <img src={product.image} alt={product.name} className="w-10 h-10 object-contain rounded" />
                          <div>
                            <p className="text-sm font-medium text-bunnie-brown">{product.name}</p>
                            <p className="text-xs text-bunnie-brown/50">{product.category}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {searchQuery.length > 1 && searchResults.length === 0 && (
                    <div className="p-4 text-sm text-bunnie-brown/50 text-center border-t border-bunnie-brown/10">
                      No products found
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <Link to="/wishlist" aria-label="Wishlist">
            <Button variant="ghost" size="icon" className="relative" aria-label="Wishlist">
              <Heart className="h-5 w-5 text-bunnie-brown" />
              <AnimatePresence>
                {wishlistCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute -top-1 -right-1 bg-bunnie-peach text-xs text-bunnie-brown w-5 h-5 rounded-full flex items-center justify-center font-medium"
                  >
                    {wishlistCount}
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>
          </Link>
          <Link to="/cart" aria-label="Cart">
            <Button variant="ghost" size="icon" className="relative" aria-label="Cart">
              <ShoppingBag className="h-5 w-5 text-bunnie-brown" />
              <AnimatePresence>
                {totalItems > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute -top-1 -right-1 bg-bunnie-peach text-xs text-bunnie-brown w-5 h-5 rounded-full flex items-center justify-center font-medium"
                  >
                    {totalItems}
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>
          </Link>
          {user ? (
            <Button
              variant="ghost"
              size="icon"
              className="text-bunnie-brown"
              onClick={async () => { await signOut(); toast("Signed out"); navigate("/"); }}
              title="Sign out"
            >
              <LogOut className="h-5 w-5" />
            </Button>
          ) : (
            <Link to="/auth">
              <Button variant="ghost" size="icon" className="text-bunnie-brown" title="Sign in">
                <UserCircle className="h-5 w-5" />
              </Button>
            </Link>
          )}
        </div>

        {/* Mobile Icons */}
        <div className="flex md:hidden items-center gap-2 absolute top-6 right-4">
          <Button variant="ghost" size="icon" onClick={() => setSearchOpen(!searchOpen)} className="text-bunnie-brown" aria-label="Search">
            <Search className="h-5 w-5" />
          </Button>
          <Link to="/wishlist" aria-label="Wishlist">
            <Button variant="ghost" size="icon" className="relative" aria-label="Wishlist">
              <Heart className="h-5 w-5 text-bunnie-brown" />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-bunnie-peach text-xs text-bunnie-brown w-5 h-5 rounded-full flex items-center justify-center font-medium">
                  {wishlistCount}
                </span>
              )}
            </Button>
          </Link>
          <Link to="/cart" aria-label="Cart">
            <Button variant="ghost" size="icon" className="relative" aria-label="Cart">
              <ShoppingBag className="h-5 w-5 text-bunnie-brown" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 bg-bunnie-peach text-xs text-bunnie-brown w-5 h-5 rounded-full flex items-center justify-center font-medium">
                  {totalItems}
                </span>
              )}
            </Button>
          </Link>
          <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle Menu">
            {mobileMenuOpen ? <X className="h-6 w-6 text-bunnie-brown" /> : <Menu className="h-6 w-6 text-bunnie-brown" />}
          </Button>
        </div>
      </nav>

      {/* Mobile Search Overlay */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-b border-bunnie-brown/10 overflow-hidden"
          >
            <div className="p-4">
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 text-sm border border-bunnie-brown/20 rounded-full focus:outline-none focus:ring-2 focus:ring-bunnie-brown/30"
                autoFocus
              />
              {searchResults.length > 0 && (
                <div className="mt-2 space-y-1">
                  {searchResults.map(product => (
                    <button
                      key={product.id}
                      onClick={() => {
                        navigate(`/product/${product.id}`);
                        setSearchOpen(false);
                        setSearchQuery("");
                      }}
                      className="w-full flex items-center gap-3 p-3 hover:bg-bunnie-peach/10 rounded-lg transition-colors text-left"
                    >
                      <img src={product.image} alt={product.name} className="w-10 h-10 object-contain rounded" />
                      <div>
                        <p className="text-sm font-medium text-bunnie-brown">{product.name}</p>
                        <p className="text-xs text-bunnie-brown/50">{product.category}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.25 }}
            className="md:hidden fixed inset-0 z-40 bg-white pt-24"
          >
            <div className="flex flex-col items-center gap-8 p-8 text-lg">
              {navLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-bunnie-brown transition-all ${location.pathname === link.to ? "font-medium" : "hover:text-opacity-70"}`}
                >
                  {link.label}
                </Link>
              ))}
              {user ? (
                <button
                  onClick={async () => { setMobileMenuOpen(false); await signOut(); toast("Signed out"); navigate("/"); }}
                  className="text-bunnie-brown/70 hover:text-bunnie-brown transition-all"
                >
                  Sign Out
                </button>
              ) : (
                <Link
                  to="/auth"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-bunnie-brown hover:text-opacity-70 transition-all font-medium"
                >
                  Sign In
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
